import { log } from "../../shared/services/debug-logger";
import { CUSTOM_OKTA_STORAGE_KEY } from "../models/oktaConstants";
import OktaAuth from "@okta/okta-auth-js";
import type { LocalToolsTokenBridge } from "../models/localToolsTokenModels";
import { isLocalToolsTokenBridgeAllowed, readLocalToolsTokenSession } from "../utils/localToolsTokenBridge.utils";

export const oktaConfig = (() => {
  const config = {
    issuer: process.env.REACT_APP_AUTH_ISSUER ?? "",
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID ?? "",
    redirectUri: `${window.location.origin}/auth/callback`,

    tokenManager: {
      // Storage (engine)
      // historically systems used cookie as their backing storage
      // this had problems for cookie storage limited to 4KB per entry
      // so instead, simply using localStorage is perfectly fine
      //
      // woah! what about security?!
      // well, it turns out that the cookie previously was _NOT_
      // a httpOnly cookie so it was never secured anyway,
      // and was always subject to inspection by any script
      // on the page
      storage: "localStorage",

      // Storage key
      // this is a randomly generated namespace
      // you should namespace your application's okta storage properties
      // so it does not clobber other applications
      storageKey: CUSTOM_OKTA_STORAGE_KEY,
    },

    // Scopes
    // Standard scopes for oidc and offline_access for a refresh token
    scopes: ["openid", "profile", "email", "address", "phone", "offline_access"],
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.__okta_config__ = config;

  return config;
})();

// The okta auth object.
// Used for `@okta/okta-auth-js`, `@okta/okta-react`, `@okta/okta-signin-widget`.
export const oktaAuth = new OktaAuth(oktaConfig);

// The optional local extension calls this page hook only after the user connects an environment.
// The existing SDK remains the sole owner of refresh tokens, rotation and cross-tab renewal leadership.
// No token is pushed to localhost by this page, and nothing is exposed on Local or Stage.
if (isLocalToolsTokenBridgeAllowed(oktaConfig.issuer, oktaConfig.clientId)) {
  const bridge: LocalToolsTokenBridge = Object.freeze({
    version: 1 as const,
    readSession: async () => readLocalToolsTokenSession(
      oktaAuth.tokenManager.getTokensSync().accessToken,
      oktaConfig.issuer,
      oktaConfig.clientId,
      oktaAuth.serviceManager.isLeader(),
    ),
    renewSession: async () => {
      const before = oktaAuth.tokenManager.getTokensSync().accessToken;

      if (!before || before.pendingRemove === true || !oktaAuth.serviceManager.isLeader()) {
        throw new Error("Keep the signed-in OpsLink leader tab open to renew the local tools session.");
      }

      try {
        await oktaAuth.tokenManager.renew("accessToken");
      } catch {
        throw new Error("OpsLink could not renew this login. Sign in again and reconnect the extension.");
      }

      const current = oktaAuth.tokenManager.getTokensSync().accessToken;

      if (!current || current.claims.sub !== before.claims.sub) {
        throw new Error("The OpsLink account changed. Reconnect the extension with the current login.");
      }

      return readLocalToolsTokenSession(
        current,
        oktaConfig.issuer,
        oktaConfig.clientId,
        oktaAuth.serviceManager.isLeader(),
      );
    },
  });
  Object.defineProperty(window, "__OPSLINK_LOCAL_TOOLS__", { value: bridge });
}

// Okta event logger. Only an example.
oktaAuth.tokenManager.on("renewed", (key, newToken, oldToken) => {
  log("SERV - oktaAuth - SUB-E renewed");
});
